import React, { FC, useEffect, useLayoutEffect, useState } from "react"
import Loading from "../../../../ui/Loading"
import { KButton, KSpan, KInput } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { crawlers } from "../../../../../apis"
import { DrmSummary, TakedownLog } from "../../../../../types"
import { mapListToCamelCase } from "../../../../../utility"

import { connect } from "react-redux"
import { RootState } from "../../../../../store"
import { SetURLSearchParams } from "react-router-dom"
import SearchUserComponent from "../../../../ui/components/SearchUserComponent"
import Pagination from "../../../../ui/table/Pagination"
import AdminReleaseTakedownLog from "./AdminReleaseTakedownLog"

const columnWidths: any = {
  ID: "4%",
  USER: "16%",
  CREATED: "16%",
  PROGRESS: "8%",
  STATUS: "8%",
  TOTAL: "12%",
  SPOTIFY: "12%",
  APPLE: "12%",
  YOUTUBE: "11%"
}

type AdminTakedownLogProps = {
  params: URLSearchParams
  setParams: SetURLSearchParams
  headerDiv: JSX.Element
  drmSummary?: DrmSummary
  // Set when the screen is opened from a Claim Log, to show the check that was run for that one
  // claim. Read the same way drmSummary is, and only one of the two is ever set.
  claimLogId?: number
}

const AdminTakedownLog: FC<AdminTakedownLogProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState<KSelectOption>()
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [takedownLogList, setTakedownLogList] = useState<TakedownLog[]>([])
  const [count, setCount] = useState(0)
  const [selectedTakedownLog, setSelectedTakedownLog] = useState<TakedownLog>()

  const scrollPosRef = React.useRef(0)

  useLayoutEffect(() => {
    if (!loading && scrollPosRef.current > 0) {
      window.scrollTo({
        top: scrollPosRef.current,
        behavior: "auto"
      })
    }
  }, [takedownLogList])

  const fetchTakedownLogList = (offset: number) => {
    const usernameParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const drmSummaryParam = props.drmSummary ? `&drm_summary_id=${props.drmSummary.id}` : ""
    const claimLogParam = props.claimLogId ? `&claim_log_id=${props.claimLogId}` : ""

    setLoading(true)
    crawlers(
      `/takedown-logs/?offset=${offset}&limit=${limit}${usernameParam}${drmSummaryParam}${claimLogParam}`,
      "GET"
    ).then(
      (resp) => {
        setTakedownLogList(mapListToCamelCase(resp.data.results))
        setCount(resp.data.count)
        setLoading(false)
      }
    )
  }

  useEffect(() => {
    fetchTakedownLogList(0)
  }, [])

  const renderTotals = (takedownLog: TakedownLog) => {
    const visibleCount = Math.min(
      takedownLog.spotifyVisibleCount || 0,
      takedownLog.appleVisibleCount || 0,
      takedownLog.youtubeVisibleCount || 0
    )
    const completeCount = Math.min(
      takedownLog.spotifyCompleteCount || 0,
      takedownLog.appleCompleteCount || 0,
      takedownLog.youtubeCompleteCount || 0
    )
    return (
      <div style={{ width: columnWidths.TOTAL }} className="items-center">
        <div className="flex gap-7">
          <KSpan text="Visible: " />
          <KSpan text={visibleCount?.toString() || "0"} />
        </div>
        <div className="flex gap-2">
          <KSpan text="Complete: " />
          <KSpan text={completeCount?.toString() || "0"} />
        </div>
        <div className="flex gap-5">
          <KSpan text="Release: " />
          <KSpan text={takedownLog.releaseCount?.toString() || "0"} />
        </div>
      </div>
    )
  }

  const renderTakedownLogs = () => {
    return (
      <div className="pt-4 pb-4" style={{ display: "flex", flexDirection: "column" }}>
        <div className="flex pb-2 gap-2" style={{ width: "100%" }}>
          {Object.keys(columnWidths).map((header) => {
            return (
              <div key={`header_${header}`} style={{ width: columnWidths[header] }}>
                <KSpan fontWeight={500} text={header.replaceAll("_", " ")} />
              </div>
            )
          })}
        </div>
        {takedownLogList.map((takedownLog) => {
          return (
            <div
              key={`takedown_log_${takedownLog.id}`}
              data-testid="admin-takedown-log-row"
              style={{ width: "100%" }}
              className="pt-5 cursor-pointer"
              onClick={() => setSelectedTakedownLog(takedownLog)}
            >
              <div className="flex pt-2 gap-2" style={{ width: "100%", borderTop: "1px dashed grey" }}>
                <div style={{ width: columnWidths.ID }} className="flex gap-1 items-center">
                  <KSpan text={takedownLog.id?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.USER }} className="flex gap-1 items-center">
                  <KSpan text={`${takedownLog.user?.firstName || "-"} ${takedownLog.user?.lastName || "-"}`} />
                </div>
                <div style={{ width: columnWidths.CREATED }} className="flex gap-1 items-center">
                  <KSpan text={takedownLog.created?.substring(0, 19)?.replace("T", " ") || "-"} />
                </div>
                <div style={{ width: columnWidths.PROGRESS }} className="flex gap-1 items-center">
                  <KSpan text={takedownLog.progress || "-"} />
                </div>
                <div style={{ width: columnWidths.STATUS }} className="flex gap-1 items-center">
                  <KSpan text={takedownLog.status} />
                </div>
                {renderTotals(takedownLog)}
                <div style={{ width: columnWidths.SPOTIFY }} className="items-center">
                  <div className="flex gap-7">
                    <KSpan text="Visible: " />
                    <KSpan text={takedownLog.spotifyVisibleCount?.toString() || "0"} />
                  </div>
                  <div className="flex gap-2">
                    <KSpan text="Complete: " />
                    <KSpan text={takedownLog.spotifyCompleteCount?.toString() || "0"} />
                  </div>
                  <div className="flex" style={{ gap: 43 }}>
                    <KSpan text="Split: " />
                    <KSpan text={takedownLog.spotifySplitExistCount?.toString() || "0"} />
                  </div>
                </div>
                <div style={{ width: columnWidths.APPLE }} className="items-center">
                  <div className="flex gap-7">
                    <KSpan text="Visible: " />
                    <KSpan text={takedownLog.appleVisibleCount?.toString() || "0"} />
                  </div>
                  <div className="flex gap-2">
                    <KSpan text="Complete: " />
                    <KSpan text={takedownLog.appleCompleteCount?.toString() || "0"} />
                  </div>
                </div>
                <div style={{ width: columnWidths.YOUTUBE }} className="items-center">
                  <div className="flex gap-7">
                    <KSpan text="Visible: " />
                    <KSpan text={takedownLog.youtubeVisibleCount?.toString() || "0"} />
                  </div>
                  <div className="flex gap-2">
                    <KSpan text="Complete: " />
                    <KSpan text={takedownLog.youtubeCompleteCount?.toString() || "0"} />
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const applyFilters = () => {
    setOffset(0)
    fetchTakedownLogList(0)
  }

  const onClickPagination = (pageNumber: number) => {
    setOffset((pageNumber - 1) * limit)
    fetchTakedownLogList((pageNumber - 1) * limit)
  }

  const onRunExisting = () => {
    setSelectedTakedownLog(undefined)
    fetchTakedownLogList(0)
  }

  const onAbortTakedownLog = (takedownLogId: number) => {
    setSelectedTakedownLog(undefined)
    crawlers(`/takedown-logs/update_takedown_log_status/?takedown_log_id=${takedownLogId}&status=aborted`, "PUT").then(
      (resp) => fetchTakedownLogList(0)
    )
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        {props.headerDiv}
        {selectedTakedownLog ? (
          <div>
            <KButton height="35px" text="Back" onClick={() => setSelectedTakedownLog(undefined)} />
          </div>
        ) : (
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <KSpan text="User" />
              <SearchUserComponent className="relative" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
            </div>
            <div>
              <KButton
                height="35px"
                text="Apply Filters"
                background="black"
                textColor="white"
                onClick={() => applyFilters()}
              />
            </div>
          </div>
        )}
      </div>
      {selectedTakedownLog ? (
        <AdminReleaseTakedownLog
          takedownLog={selectedTakedownLog}
          onRunExisting={onRunExisting}
          onAbortTakedownLog={onAbortTakedownLog}
          isDrm={props.drmSummary ? true : false}
        />
      ) : (
        renderTakedownLogs()
      )}
      {!selectedTakedownLog && (
        <div>
          <Pagination
            key={""}
            hidden={false}
            size={"sm"}
            pageCount={Math.ceil(count / limit)}
            currentPage={offset / limit + 1}
            onClickPage={onClickPagination}
          />
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(AdminTakedownLog)
